import { ButtonProtected } from '@components'

import { Alert, Flex, Spacing, Text } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Scope } from '../../../../enums/scope'
import { TrackingContext } from '../../../../enums/trackingContext'
import { useBankIntegration } from '../../contexts/bankIntegrationContext'
import { useSyncOpenBankingConnection } from '../../hooks/useSyncOpenBankingConnection'

export const ConnectionErrorAlert = (): ReactElement | null => {
  const { t } = useTranslation()
  const { isLoading: isLoadingSyncOpenBankingConnection, syncOpenBankingAccountConnection } =
    useSyncOpenBankingConnection()
  const { connectedBankAccountId, hasBankConnectionError, isLoading, isOrphaned, requiresBankReconnection } =
    useBankIntegration()

  const shouldShowConnectButton = !isOrphaned && !hasBankConnectionError

  const handleConnectBankClick = useCallback(() => {
    syncOpenBankingAccountConnection(connectedBankAccountId as string)
  }, [connectedBankAccountId, syncOpenBankingAccountConnection])

  const errorMessage = useMemo(() => {
    if (isOrphaned || hasBankConnectionError) {
      return t('bank_connection_alert.connection_error')
    }

    return t('bank_connection_alert.default_text')
  }, [hasBankConnectionError, isOrphaned, t])

  if (isLoading || (!isOrphaned && !hasBankConnectionError && !requiresBankReconnection)) {
    return null
  }

  return (
    <Alert>
      <Flex alignItems="center" gap={Spacing.S8}>
        <Text weight="bold">{errorMessage}</Text>
        {shouldShowConnectButton && (
          <ButtonProtected
            size="sm"
            onClick={handleConnectBankClick}
            variant="onColor"
            color="warning"
            loading={isLoadingSyncOpenBankingConnection}
            scopes={Scope.BankSyncWrite}
            trackingContext={TrackingContext.BankConnectionsConnectBank}
          >
            {t('bank_connection_alert.button')}
          </ButtonProtected>
        )}
      </Flex>
    </Alert>
  )
}
